import {createSlice} from '@reduxjs/toolkit';
import {
  HOURS_PER_SHIFT_IN_CASE,
  HOURS_PER_SHIFT_COST,
  STEPS_NUMBERS,
} from 'src/shared/constants/AppConst';
import {BookingMarkupReference} from 'src/shared/constants/AppEnums';
import {BookingShiftsDto} from 'src/shared/services/booking/dto/booking-shifts-dto';
import {CaseBalanceDetailsDto} from 'src/shared/services/cases/dto/case-balance-details-dto';
import {CaseDetailsDto} from 'src/shared/services/cases/dto/details-case-dto';

interface CasesInitialState {
  caseDetails: CaseDetailsDto | undefined;
  currentTabInCaseDetails: number;
  currentTabInSchedule: number;
  newStartDate: any;
  refreshCaregiverResponses: boolean;
  totalBookingCost?: number;
  plannedMarkupInCase?: number;
  manuallyMarkupInCase?: number;
  markupReferenceInCase?: number;
  spanDays?: number;
  markupType?: number;
  shiftsDetails: Array<BookingShiftsDto>;
  displayShifts: Boolean;
  hoursPerShiftCost: number;
  hoursPerShift: number;
  openQuotationModal: boolean;
  totalCaregiverBudget: number;
  oldHoursPerShiftCost: number;
  oldHoursPerShift: number;
  newNotificationForCaseBalance: any;
  isCaseBalanceDetailsLoading?: boolean;
  caseBalanceDetails: Array<CaseBalanceDetailsDto>;
  pageSizeCaseBalance: number;
  currentPageCaseBalance: number;
  totalTableCaseBalance: number;
}

const initialState: CasesInitialState = {
  caseDetails: undefined,
  currentTabInCaseDetails: STEPS_NUMBERS.One,
  currentTabInSchedule: STEPS_NUMBERS.One,
  newStartDate: undefined,
  refreshCaregiverResponses: false,
  totalBookingCost: 0,
  plannedMarkupInCase: 0,
  manuallyMarkupInCase: undefined,
  markupReferenceInCase: BookingMarkupReference.planned,
  spanDays: 0,
  markupType: 0,
  shiftsDetails: [],
  displayShifts: false,
  hoursPerShiftCost: HOURS_PER_SHIFT_COST,
  hoursPerShift: HOURS_PER_SHIFT_IN_CASE,
  openQuotationModal: false,
  totalCaregiverBudget: 0,
  oldHoursPerShiftCost: HOURS_PER_SHIFT_COST,
  oldHoursPerShift: HOURS_PER_SHIFT_IN_CASE,
  newNotificationForCaseBalance: undefined,
  isCaseBalanceDetailsLoading: false,
  caseBalanceDetails: [],
  pageSizeCaseBalance: 30,
  currentPageCaseBalance: 1,
  totalTableCaseBalance: 0,
};

const casesSlice = createSlice({
  name: 'cases',
  initialState: initialState,
  reducers: {
    setCaseDetails: (state, action) => {
      state.caseDetails = action.payload;
    },
    setCurrentTabInCaseDetails: (state, action) => {
      state.currentTabInCaseDetails = action.payload;
    },
    setCurrentTabInSchedule: (state, action) => {
      state.currentTabInSchedule = action.payload;
    },
    setNewStartDate: (state, action) => {
      state.newStartDate = action.payload;
    },
    setRefreshCaregiverResponses: (state, action) => {
      state.refreshCaregiverResponses = action.payload;
    },
    setTotalBookingCost: (state, action) => {
      state.totalBookingCost = action.payload;
    },
    setPlannedMarkupInCase: (state, action) => {
      state.plannedMarkupInCase = action.payload;
    },
    setManuallyMarkupInCase: (state, action) => {
      state.manuallyMarkupInCase = action.payload;
    },
    setMarkupReferenceInCase: (state, action) => {
      state.markupReferenceInCase = action.payload;
    },
    setSpanDaysInCase: (state, action) => {
      state.spanDays = action.payload;
    },
    setMarkupTypeInCase: (state, action) => {
      state.markupType = action.payload;
    },
    setShiftsDetailsInCase: (state, action) => {
      state.shiftsDetails = action.payload;
    },
    setDisplayShifts: (state, action) => {
      state.displayShifts = action.payload;
    },
    setHoursPerShiftCostInCase: (state, action) => {
      state.hoursPerShiftCost = action.payload;
    },
    setHoursPerShiftInCase: (state, action) => {
      state.hoursPerShift = action.payload;
    },
    setOpenQuotationModal: (state, action) => {
      state.openQuotationModal = action.payload;
    },
    setTotalCaregiverBudget: (state, action) => {
      state.totalCaregiverBudget = action.payload;
    },
    setOldHoursPerShiftCostInCase: (state, action) => {
      state.oldHoursPerShiftCost = action.payload;
    },
    setOldHoursPerShiftInCase: (state, action) => {
      state.oldHoursPerShift = action.payload;
    },
    setNewNotificationForCaseBalance: (state, action) => {
      state.newNotificationForCaseBalance = action.payload;
    },
    setIsCaseBalanceDetailsLoading: (state, action) => {
      state.isCaseBalanceDetailsLoading = action.payload;
    },
    setCaseBalanceDetails: (state, action) => {
      state.caseBalanceDetails = action.payload;
    },
    setTotalTableCaseBalance: (state, action) => {
      state.totalTableCaseBalance = action.payload;
    },
    setCurrentPageTableCaseBalance: (state, action) => {
      state.currentPageCaseBalance = action.payload;
    },
    setPageSizeTableCaseBalance: (state, action) => {
      state.pageSizeCaseBalance = action.payload;
    },
  },
});

export const {
  setCaseDetails,
  setCurrentTabInCaseDetails,
  setCurrentTabInSchedule,
  setNewStartDate,
  setRefreshCaregiverResponses,
  setTotalBookingCost,
  setPlannedMarkupInCase,
  setManuallyMarkupInCase,
  setMarkupReferenceInCase,
  setSpanDaysInCase,
  setMarkupTypeInCase,
  setShiftsDetailsInCase,
  setDisplayShifts,
  setHoursPerShiftCostInCase,
  setHoursPerShiftInCase,
  setOpenQuotationModal,
  setTotalCaregiverBudget,
  setOldHoursPerShiftCostInCase,
  setOldHoursPerShiftInCase,
  setNewNotificationForCaseBalance,
  setIsCaseBalanceDetailsLoading,
  setCaseBalanceDetails,
  setTotalTableCaseBalance,
  setCurrentPageTableCaseBalance,
  setPageSizeTableCaseBalance,
} = casesSlice.actions;

export default casesSlice.reducer;
